import apiClient from "../api/apiClient"
import { LoadForm, CreateLoadFormForm, BookerResponse, ApiResponse, LoadFormPreviewSummary } from "../../types"
import type { ChequeDetails } from '../../types/cheque'

export const loadFormService = {
  // Get all bookers
  async getBookers(): Promise<BookerResponse> {
    const response = await apiClient.get<BookerResponse>("/admin/users/get-all?type=booker")
    return response.data
  },

  // Get active bills for selected bookers (for preview)
  async getActiveBillsForBookers(bookerIds: string[]): Promise<any[]> {
    // Get billing data
    const response = await apiClient.get<any>("/admin/billing/get-all", {
      params: {
        booker_id:bookerIds,
        status: "active"
      }
    })
    console.log('Billing response:', response.data)
    
    // Return the bills array directly
    return Array.isArray(response.data) ? response.data : (response.data.results || [])
  },

  // Get preview summary for selected bookers
  async getLoadFormPreviewSummary(bookerIds: string[]): Promise<LoadFormPreviewSummary[]> {
    const response = await apiClient.get<{ summaries: LoadFormPreviewSummary[] }>(
      "/admin/load-form/preview-summary",
      {
        params: {
          booker_ids: bookerIds
        }
      }
    )
    return response.data.summaries
  },

  // Create load form
  async createLoadForm(formData: CreateLoadFormForm): Promise<LoadForm> {
    const response = await apiClient.post<ApiResponse<LoadForm>>("/admin/load-form/create-bulk", {
      booker_ids: formData.bookerIds,
      delivery_date: formData.deliveryDate,
      selections: formData.selections
    })
    return response.data.data || response.data
  },

  // Get all load forms
  async getLoadForms(params?: {
    page?: number
    limit?: number
    search?: string
    bookerId?: string
    status?: string
    area?: string
    date?: string
    startDate?: string
    endDate?: string
  }): Promise<any> {
    // Transform bookerId to booker_id and area to area_id for API
    const apiParams: any = { ...params }
    if (apiParams.bookerId) {
      apiParams.booker_id = apiParams.bookerId
      delete apiParams.bookerId
    }
    if (apiParams.area) {
      apiParams.area_id = apiParams.area
      delete apiParams.area
    }
    
    const response = await apiClient.get<any>("/admin/load-form", { params: apiParams })
    console.log('Load forms response:', response.data)
    return response.data
  },

  // Get load form by ID
  async getLoadFormById(id: string): Promise<LoadForm> {
    const response = await apiClient.get<ApiResponse<LoadForm>>(`/loadforms/${id}`)
    return response.data.data
  },

  // Get load form with history
  async getLoadFormHistory(id: string): Promise<any> {
    const response = await apiClient.get<any>(`/admin/load-form/${id}/history`)
    return response.data
  },

  // Update load form status
  async updateLoadFormStatus(id: string, status: 'open' | 'closed'): Promise<LoadForm> {
    const response = await apiClient.patch<ApiResponse<LoadForm>>(`/loadforms/${id}/status`, { status })
    return response.data.data
  },

  // Delete load form
  async deleteLoadForm(id: string): Promise<void> {
    await apiClient.delete(`/admin/load-form/${id}`)
  },

  // Mark load form as pending
  async markLoadFormAsPending(id: string, reason?: string): Promise<any> {
    const response = await apiClient.post(`/admin/load-form/mark-pending/${id}`, { reason })
    return response.data
  },

  // Print load form HTML
  async printLoadFormHtml(id: string): Promise<string> {
    const response = await apiClient.get<string>(`/admin/load-form/${id}/print-html`)
    return response.data
  },

  // Print load form with invoices HTML
  async printLoadFormWithInvoicesHtml(id: string): Promise<string> {
    const response = await apiClient.get<string>(`/admin/load-form/${id}/print-with-invoices`)
    return response.data
  },

  // Print only invoices HTML for a load form
  async printLoadFormInvoicesOnlyHtml(id: string): Promise<string> {
    const response = await apiClient.get<string>(`/admin/load-form/${id}/print-invoices-only`)
    return response.data
  },

  // Cancel bill in load form
  async cancelBillInLoadForm(loadFormId: string, billId: string): Promise<void> {
    await apiClient.delete(`/admin/load-form/${loadFormId}/bills/${billId}`)
  },

  // Recalculate load form totals
  async recalculateLoadFormTotals(loadFormId: string): Promise<any> {
    const response = await apiClient.put(`/admin/load-form/${loadFormId}/recalculate`)
    return response.data
  },

  // Settle load form
  async settleLoadForm(loadFormId: string, settlementData: {
    cash_handed_over: number
    cash_collected: number
    sales_on_credit: number
    credit_sales: Array<{
      customer_id: string
      customer_name: string
      amount: number
      bill_id: string
      remarks?: string
      cheque?: ChequeDetails
    }>
    cancelled_bills: string[]
    daily_expense: number
    expense_remarks: string
    unsettled_amount: number
    unsettled_reason: string
    settled_at?: string // optional ISO date string (YYYY-MM-DD); defaults to current date on backend
  }): Promise<any> {
    const response = await apiClient.post(`/admin/load-form/settle/${loadFormId}`, settlementData)
    return response.data
  },

  // Update bill
  async updateBill(billId: string, billData: {
    customer_id: string
    sku_items: Array<{
      sku_id: string
      quantity_ctn: number
      loose_pcs?: number
    }>
    remarks: string
  }): Promise<any> {
    const response = await apiClient.put(`/admin/billing/${billId}`, billData)
    return response.data
  },
}
